import { ADMIN_ROLES, Role } from "@/shared/types/role";

/**
 * Single source of truth for WHICH INTERNAL ROLE MAY OPEN WHICH SECTION of the
 * backoffice. The Sidebar hides the entries and the router guards the routes
 * from this one table, so the two can never disagree.
 *
 * Every list mirrors the API's gate on the routes the section reads on mount
 * (`@Roles(...)` or `internalRoles` of `@RequirePermission`, wanderfit-api
 * `origin/v2.0`); a section listed for a role must never answer 403 on its
 * first read. The UI never enforces business authorization — the API does —
 * this only decides what to render. Sections gated on a `/auth/me`
 * permission (Invitations, Organizations) are not here: they use
 * `Permission` / `RequirePermission` instead (ADR-0017).
 *
 * The legacy role-literal gates still live elsewhere (in-page buttons, see
 * CLAUDE.md "Access model"); they are migrated to permissions as the API
 * moves its routes. New sections add a row here, never a role list inline.
 */
export const Section = {
  /** Static placeholder: no read of its own. */
  DASHBOARD: "dashboard",
  /** `GET /admin/venues/pending` and the approve/reject/request-changes writes: ADMIN + SUPPORT (`venues.controller.ts`). */
  VENUE_APPROVAL: "venueApproval",
  /** `GET /admin/venues[/:id][/qr/download]`: `internalRoles` ADMIN + SUPPORT (`admin-venues.controller.ts`). */
  VENUE_DIRECTORY: "venueDirectory",
  /** `GET|PATCH /admin/properties[/:id]`: `internalRoles` ADMIN + SUPPORT (`admin-properties.controller.ts`). */
  PROPERTIES: "properties",
  /** `GET /admin/users[/:id]`: ADMIN + SUPPORT (`admin-users.controller.ts`). */
  USERS: "users",
  /** Every `/admin/tos*` route, catalog and document types included: ADMIN (`admin-tos*.controller.ts`). */
  COMPLIANCE: "compliance",
  /** `/admin/communications*`: ADMIN (`admin-communications.controller.ts`). */
  COMMUNICATIONS: "communications",
  /** `GET /admin/subscriptions[/:id]`: ADMIN + SUPPORT + FINANCE (`admin-subscriptions.controller.ts`). */
  SUBSCRIPTIONS: "subscriptions",
  /** `GET /admin/founding/*` reports: ADMIN + FINANCE; the writes are ADMIN and gated in-component (`founding-admin.controller.ts`). */
  FOUNDING: "founding",
  /** `/admin/subscription-tiers`: ADMIN (`admin-subscription-tiers.controller.ts`). */
  SUBSCRIPTION_TIERS: "subscriptionTiers",
  /** `/admin/feature-flags*`: ADMIN + SUPPORT at the controller; `refresh` is ADMIN (`feature-flag-admin.controller.ts`). */
  FEATURE_FLAGS: "featureFlags",
  /** `GET /admin/catalog/*`: ADMIN + FINANCE; the writes are ADMIN (`admin-catalog.controller.ts`). */
  CATALOG: "catalog",
  /** `/admin/subscriptions/drift-alerts`: ADMIN + FINANCE (`admin-drift-alerts.controller.ts`). */
  DRIFT_ALERTS: "driftAlerts",
  /** Payouts, reports, accruals, ledger and corrections reads: ADMIN + FINANCE; Pay/Liquidate are ADMIN in-component (`admin-financial.controller.ts`). */
  FINANCE: "finance",
} as const;

export type Section = (typeof Section)[keyof typeof Section];

export const SECTIONS: readonly Section[] = Object.values(Section);

const ADMIN_ONLY = [Role.WANDERFIT_ADMIN] as const;
const ADMIN_AND_SUPPORT = [Role.WANDERFIT_ADMIN, Role.WANDERFIT_SUPPORT] as const;
const ADMIN_AND_FINANCE = [Role.WANDERFIT_ADMIN, Role.WANDERFIT_FINANCE] as const;

export const SECTION_ROLES: Readonly<Record<Section, readonly Role[]>> = {
  [Section.DASHBOARD]: ADMIN_ROLES,
  [Section.VENUE_APPROVAL]: ADMIN_AND_SUPPORT,
  [Section.VENUE_DIRECTORY]: ADMIN_AND_SUPPORT,
  [Section.PROPERTIES]: ADMIN_AND_SUPPORT,
  [Section.USERS]: ADMIN_AND_SUPPORT,
  [Section.COMPLIANCE]: ADMIN_ONLY,
  [Section.COMMUNICATIONS]: ADMIN_ONLY,
  [Section.SUBSCRIPTIONS]: ADMIN_ROLES,
  [Section.FOUNDING]: ADMIN_AND_FINANCE,
  [Section.SUBSCRIPTION_TIERS]: ADMIN_ONLY,
  [Section.FEATURE_FLAGS]: ADMIN_AND_SUPPORT,
  [Section.CATALOG]: ADMIN_AND_FINANCE,
  [Section.DRIFT_ALERTS]: ADMIN_AND_FINANCE,
  [Section.FINANCE]: ADMIN_AND_FINANCE,
};

/** Whether a session with `role` may open `section`. No role means nothing (fails closed). */
export function canAccessSection(role: Role | null | undefined, section: Section): boolean {
  return role != null && SECTION_ROLES[section].includes(role);
}

/** Every section `role` may open (the Sidebar's view of the table). */
export function sectionsFor(role: Role | null | undefined): readonly Section[] {
  return SECTIONS.filter((section) => canAccessSection(role, section));
}
